import { useCallback, useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useMe } from "@/context/MeContext";
import { errMsg, fmtDate, num, publicGet, useApi } from "@/lib/api";

export function AdminWholesale() {
  const api = useApi();
  const { config } = useMe();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [ship, setShip] = useState({ shipping_cost: 0, pickup: false, address: "" });

  const load = useCallback(async () => {
    const d = await api("get", "/admin/wholesale");
    setInfo(d);
    setShip({ shipping_cost: d.partner.shipping_cost ?? 0, free_shipping_over: d.partner.free_shipping_over ?? 0, pickup: !!d.partner.pickup, address: d.partner.address || "" });
  }, [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const r = await api("post", "/admin/wholesale/import", { text: await file.text() });
      setResult(r);
      if (config) Object.assign(config, await publicGet("/public/config"));
      toast.success(`Listino caricato: ${num(r.rows)} prodotti`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const saveShip = async () => {
    try {
      await api("put", "/admin/wholesale/settings", { ...ship, shipping_cost: parseFloat(ship.shipping_cost || 0), free_shipping_over: parseFloat(ship.free_shipping_over || 0) });
      toast.success("Spedizione salvata");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!info) return <p className="text-slate-500">Caricamento…</p>;
  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-wholesale">
      <div className="grid gap-4 sm:grid-cols-3">
        {[["Prodotti a catalogo", num(info.total)], ["Disponibili", num(info.in_stock)], ["Ultimo caricamento", info.last_import ? fmtDate(info.last_import.created_at) : "—"]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-white/10 bg-[#12141C] p-4">
            <p className="text-xs uppercase tracking-widest text-slate-500">{l}</p>
            <p className="mt-1 font-display text-2xl font-bold text-white">{v}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#12141C] p-5">
        <p className="font-semibold text-white">Carica o aggiorna il listino</p>
        <p className="mt-1 text-sm text-slate-400">
          Formato del fornitore (campi separati da <code>|</code>, righe chiuse da <code>&lt;endrecord&gt;</code>). Ricaricando il file i prodotti vengono aggiornati tramite il codice articolo: prezzi, stock e disponibilità. I prodotti che non sono più nel file vanno a stock 0.
        </p>
        <p className="mt-2 text-sm text-slate-400">Prezzi = prezzo del file + <b className="text-[#CD7F32]">20% Bronze</b> · <b className="text-amber-400">15% Gold</b> · <b className="text-sky-400">5% Diamond</b>. Pubblicati subito nella categoria «Ingrosso elettronica» con il triplo dei punti.</p>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-black hover:bg-amber-400">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Caricamento…" : "Scegli file listino"}
          <input data-testid="wholesale-file-input" type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" disabled={busy} onChange={onFile} />
        </label>
        {result && (
          <p data-testid="wholesale-result" className="mt-3 text-sm text-emerald-400">
            {num(result.rows)} prodotti letti · {num(result.created)} nuovi · {num(result.updated)} aggiornati · {num(result.zeroed)} azzerati · {result.subcategories} sottocategorie
          </p>
        )}
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#12141C] p-5">
        <p className="font-semibold text-white">Consegna dei tuoi prodotti</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div><Label className="text-xs text-slate-400">Costo spedizione per ordine (€)</Label><Input data-testid="wholesale-shipping" type="number" step="0.01" value={ship.shipping_cost} onChange={(e) => setShip({ ...ship, shipping_cost: e.target.value })} className="mt-1 bg-[#1C1F2C]" /></div>
          <div><Label className="text-xs text-slate-400">Spedizione gratuita sopra (€, 0 = mai)</Label><Input data-testid="wholesale-free-over" type="number" step="0.01" value={ship.free_shipping_over} onChange={(e) => setShip({ ...ship, free_shipping_over: e.target.value })} className="mt-1 bg-[#1C1F2C]" /></div>
          <div><Label className="text-xs text-slate-400">Indirizzo per il ritiro</Label><Input data-testid="wholesale-address" value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })} className="mt-1 bg-[#1C1F2C]" /></div>
          <div className="flex items-center gap-3"><Switch data-testid="wholesale-pickup" checked={ship.pickup} onCheckedChange={(v) => setShip({ ...ship, pickup: v })} /><span className="text-sm text-slate-300">Ritiro disponibile</span></div>
        </div>
        <button data-testid="wholesale-save-shipping" onClick={saveShip} className="mt-4 rounded-full border border-white/15 px-5 py-2 text-sm text-white hover:bg-white/10">Salva</button>
      </div>
    </div>
  );
}
